import type { PowerInterestData, PowerInterestPoint } from '@integratio/core';
import { IsoLegend } from './IsoLegend';

interface Props {
  data: PowerInterestData;
  total: number;
  print?: boolean;
}

const WIDTH = 1000;
const HEIGHT_SCREEN = 620;
const HEIGHT_PRINT = 520;
const MARGIN = { top: 46, right: 40, bottom: 92, left: 84 };

interface Cluster {
  key: string;
  poder: number;
  interesses: number;
  color: string;
  grupo: string;
  items: PowerInterestPoint[];
}

function clusterPoints(points: PowerInterestPoint[]): Cluster[] {
  const map = new Map<string, Cluster>();
  for (const point of points) {
    const key = `${point.poder}|${point.interesses}`;
    const existing = map.get(key);
    if (existing) {
      existing.items.push(point);
      continue;
    }
    map.set(key, {
      key,
      poder: point.poder,
      interesses: point.interesses,
      color: point.color,
      grupo: point.grupo,
      items: [point],
    });
  }
  return [...map.values()].sort((a, b) => b.items.length - a.items.length);
}

export function PowerInterestMatrix({ data, total, print = false }: Props): JSX.Element {
  const HEIGHT = print ? HEIGHT_PRINT : HEIGHT_SCREEN;
  const clusters = clusterPoints(data.points);
  const [poderMin, poderMax] = data.poderDomain;
  const [interesseMin, interesseMax] = data.interessesDomain;

  const plotWidth = WIDTH - MARGIN.left - MARGIN.right;
  const plotHeight = HEIGHT - MARGIN.top - MARGIN.bottom;

  const scaleX = (value: number): number =>
    MARGIN.left + ((value - poderMin) / Math.max(1e-6, poderMax - poderMin)) * plotWidth;
  const scaleY = (value: number): number =>
    MARGIN.top + plotHeight - ((value - interesseMin) / Math.max(1e-6, interesseMax - interesseMin)) * plotHeight;

  const midX = scaleX(data.poderMid);
  const midY = scaleY(data.interessesMid);

  const quadrantLabels = data.quadrants.map((group) => {
    // row 0 = influência/poder alto; col 1 = interesses altos
    const row = group.matrix?.row ?? 0;
    const col = group.matrix?.col ?? 0;
    const x = col === 1 ? (midX + MARGIN.left + plotWidth) / 2 : (MARGIN.left + midX) / 2;
    const y = row === 0 ? (MARGIN.top + midY) / 2 : (midY + MARGIN.top + plotHeight) / 2;
    return { group, x, y };
  });

  return (
    <div className={print ? 'grid grid-cols-[320px_1fr] gap-8' : 'grid gap-8 xl:grid-cols-[300px_1fr]'}>
      <IsoLegend groups={data.quadrants} total={total} limit={print ? Infinity : 8} showNames={!print} />

      <div className="min-w-0">
        <svg viewBox={`0 0 ${WIDTH} ${HEIGHT}`} role="img" aria-label="Matriz Poder x Interesses" className="h-auto w-full" style={{ height: HEIGHT }}>
          <defs>
            <filter id="pi-dot" x="-50%" y="-50%" width="200%" height="200%">
              <feDropShadow dx="0" dy="3" stdDeviation="4" floodColor="#1B1A20" floodOpacity="0.22" />
            </filter>
          </defs>

          {/* Fundo dos quadrantes */}
          {data.quadrants.map((group) => {
            const row = group.matrix?.row ?? 0;
            const col = group.matrix?.col ?? 0;
            const x = col === 1 ? midX : MARGIN.left;
            const y = row === 0 ? MARGIN.top : midY;
            const w = col === 1 ? MARGIN.left + plotWidth - midX : midX - MARGIN.left;
            const h = row === 0 ? midY - MARGIN.top : MARGIN.top + plotHeight - midY;
            return (
              <rect key={group.key} x={x} y={y} width={Math.max(0, w)} height={Math.max(0, h)} fill={group.color} opacity="0.06" />
            );
          })}

          {/* Grade */}
          <g stroke="#E5E5EA" strokeWidth="1">
            <rect x={MARGIN.left} y={MARGIN.top} width={plotWidth} height={plotHeight} fill="none" />
            <line x1={midX} y1={MARGIN.top} x2={midX} y2={MARGIN.top + plotHeight} strokeDasharray="6 6" />
            <line x1={MARGIN.left} y1={midY} x2={MARGIN.left + plotWidth} y2={midY} strokeDasharray="6 6" />
          </g>

          {/* Rótulos de quadrante */}
          {quadrantLabels.map(({ group, x, y }) => (
            <text
              key={`q-${group.key}`}
              x={x}
              y={y}
              textAnchor="middle"
              fontSize="15"
              fontWeight="700"
              letterSpacing="1.4"
              fill={group.color}
              opacity="0.55"
              fontFamily="inherit"
            >
              {group.name}
            </text>
          ))}

          {/* Pontos agrupados por coordenada idêntica */}
          {clusters.map((cluster) => {
            const cx = scaleX(cluster.poder);
            const cy = scaleY(cluster.interesses);
            const radius = cluster.items.length > 1 ? 13 + Math.sqrt(cluster.items.length) * 4 : 11;
            return (
              <g key={cluster.key} filter="url(#pi-dot)">
                <title>
                  {`${cluster.grupo} · Poder ${cluster.poder} · Interesses ${cluster.interesses}\n${cluster.items
                    .map((item) =>
                      [item.nome, item.cargo, item.empresaInstituicao].filter(Boolean).join(' — '),
                    )
                    .join('\n')}`}
                </title>
                <circle cx={cx} cy={cy} r={radius} fill={cluster.color} stroke="#FFFFFF" strokeWidth="2.5" />
              </g>
            );
          })}

          {/* Eixos */}
          <g fontFamily="inherit" fill="#54525E" fontSize="13" fontWeight="600">
            <text x={MARGIN.left} y={MARGIN.top + plotHeight + 26}>
              {poderMin}
            </text>
            <text x={MARGIN.left + plotWidth} y={MARGIN.top + plotHeight + 26} textAnchor="end">
              {poderMax}
            </text>
            <text
              x={MARGIN.left + plotWidth / 2}
              y={MARGIN.top + plotHeight + 56}
              textAnchor="middle"
              fontSize="15"
              fontWeight="700"
              letterSpacing="1.6"
              fill="#8E8C99"
            >
              PODER →
            </text>
            <text x={MARGIN.left - 14} y={MARGIN.top + plotHeight} textAnchor="end">
              {interesseMin}
            </text>
            <text x={MARGIN.left - 14} y={MARGIN.top + 6} textAnchor="end">
              {interesseMax}
            </text>
            <text
              x={26}
              y={MARGIN.top + plotHeight / 2}
              textAnchor="middle"
              fontSize="15"
              fontWeight="700"
              letterSpacing="1.6"
              fill="#8E8C99"
              transform={`rotate(-90 26 ${MARGIN.top + plotHeight / 2})`}
            >
              INTERESSES →
            </text>
          </g>
        </svg>

        <p className="mt-2 text-center text-[11px] text-brand-slate">
          Cada bolha representa stakeholders com os mesmos valores de Poder e Interesses; o número indica a quantidade
          agrupada. As linhas tracejadas marcam o ponto médio de cada eixo.
        </p>

        {data.unplotted.length > 0 && (
          <p className="mt-2 text-[11px] text-state-attention">
            {data.unplotted.length} stakeholder(s) sem valores numéricos de Poder ou Interesses não são plotados
            {print ? '.' : ':'}{' '}
            {!print &&
              data.unplotted
                .slice(0, 6)
                .map((item) => [item.nome, item.cargo, item.empresaInstituicao].filter(Boolean).join(' — '))
                .join(', ')}
            {!print && data.unplotted.length > 6 && ` e mais ${data.unplotted.length - 6}.`}
            {!print && data.unplotted.length <= 6 && '.'}
          </p>
        )}
      </div>
    </div>
  );
}
